import { employeeApi } from "../../redux/api/axiosInstance";

export interface Notification {
  notificationId: number;
  employeeId: number;
  userId: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  title?: string;
  type?: string;
  priority?: "low" | "medium" | "high";

  /**
   * Routing hints from the server (EM migration 012).
   *
   * The server says WHAT the notification is about — `module` names the domain
   * ("LeaveRequest", "Reimbursement") and `recordId` identifies the row. It does
   * NOT decide where the user lands: routes are a client concern, and web and
   * mobile have different ones. `resolveNotificationRoute` in
   * src/utils/notificationRoute.ts turns these into an SPA path.
   *
   * `deepLink` is the server's pre-built mobile URI (e.g. "hrms://approvals/..."),
   * kept for the mobile client and deliberately NOT used for web navigation — a
   * server-built web route would drift from this app's router the moment either
   * side changed.
   */
  module?: string;
  recordId?: number;
  instanceId?: number;
  deepLink?: string;

  /**
   * Why YOU got this notification (EM migration 022): "owner" (the record is yours) or
   * "approver" (you must act on someone else's).
   *
   * `module` alone was not enough. The same LeaveRequest notifies its filer and its
   * approver, and this client used to tell them apart by the viewer's ROLE — which is
   * wrong for every manager or HR user who files their own request, sending them to the
   * queue instead of to their own request.
   *
   * Optional on purpose: absent on every pre-022 row and on anything an older build or
   * the mobile client wrote. `resolveNotificationRoute` falls back to the previous
   * role-based behaviour when it is missing, so nothing stops routing.
   */
  audience?: string;
}

export interface PaginatedNotificationResponse {
  data: Notification[];
  totalCount: number;
  pageNo: number;
  pageSize: number;
  totalPages: number;
}

export interface UnreadCountResponse {
  unreadCount: number; // Changed from 'count' to 'unreadCount'
}

const BASE = "Notifications";

const extract = <T>(p: Promise<any>): Promise<T> =>
  p.then(r => r.data).catch((err: any) => {
    const msg =
      err?.response?.data?.message ||
      err?.message ||
      "Notification service error";
    const wrapped = new Error(msg);
    (wrapped as any).status = err?.response?.status;
    throw wrapped;
  });

export const getAllNotifications = (pageNo = 1, pageSize = 10) =>
  extract<PaginatedNotificationResponse>(
    employeeApi.get(`${BASE}/GetAll`, { params: { pageNo, pageSize } })
  );

export const getNotificationById = (notificationId: number) =>
  extract<Notification>(employeeApi.get(`${BASE}/GetById/${notificationId}`));

export const getUnreadNotificationsCount = () =>
  extract<UnreadCountResponse>(employeeApi.get(`${BASE}/GetUnreadCount`));

export const markNotificationAsRead = (notificationId: number) =>
  extract<void>(employeeApi.post(`${BASE}/MarkAsRead/${notificationId}`));

export const markAllNotificationsAsRead = () =>
  extract<void>(employeeApi.post(`${BASE}/MarkAllAsRead`));